import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Stack,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  ArrowForward as ArrowForwardIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Autocomplete from '@mui/material/Autocomplete';

/**
 * Marketplace Hero Section
 * Main search interface for finding mental health professionals
 */
const MarketplaceHero = ({ onSearch, popularSearches = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');

  const handleSearch = (e) => {
    e?.preventDefault();
    const searchParams = new URLSearchParams();
    if (searchQuery.trim()) {
      searchParams.set('search', searchQuery.trim());
    }
    if (location.trim()) {
      searchParams.set('location', location.trim());
    }
    
    if (onSearch) {
      onSearch({ query: searchQuery, location });
    } else {
      navigate(`/doctors?${searchParams.toString()}`);
    }
  };

  const handlePopularSearch = (search) => {
    if (search && search.trim()) {
      navigate(`/doctors?search=${encodeURIComponent(search.trim())}`);
    }
  };

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: 'white',
        padding: { xs: theme.spacing(4, 2), md: theme.spacing(8, 4) },
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: '60vh', md: '75vh' },
        display: 'flex',
        alignItems: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Stack spacing={4} alignItems="center" textAlign="center">
            {/* Main Heading */}
            <Box>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h1"
                fontWeight={700}
                gutterBottom
                sx={{
                  textShadow: '0 2px 20px rgba(0,0,0,0.1)',
                  mb: 2,
                }}
              >
                Find Your Perfect Mental Health Professional
              </Typography>
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                component="p"
                sx={{
                  opacity: 0.95,
                  maxWidth: '700px',
                  mx: 'auto',
                  fontWeight: 300,
                }}
              >
                Connect with verified psychiatrists, psychologists, therapists, and counselors.
                Book appointments online or in-person. Get the help you need, when you need it.
              </Typography>
            </Box>

            {/* Search Box */}
            <Paper
              component="form"
              onSubmit={handleSearch}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                width: '100%',
                maxWidth: '800px',
                background: alpha(theme.palette.background.paper, 0.98),
                backdropFilter: 'blur(20px)',
                boxShadow: '0 25px 80px rgba(0, 0, 0, 0.15)',
              }}
              elevation={0}
            >
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  placeholder="Search for psychiatrist, psychologist, therapist..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PsychologyIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery('')}
                          aria-label="Clear search"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '1.1rem',
                    },
                  }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    placeholder="Location (city, state)"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch(e);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: location && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setLocation('')}
                            aria-label="Clear location"
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Search
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {/* Popular Searches */}
            {popularSearches.length > 0 && (
              <Box sx={{ width: '100%', maxWidth: '800px' }}>
                <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                  Popular searches:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {popularSearches.map((search, index) => (
                    <Chip
                      key={index}
                      label={search}
                      onClick={() => handlePopularSearch(search)}
                      sx={{
                        background: alpha(theme.palette.background.paper, 0.2),
                        color: '#ffffff',
                        border: `1px solid ${alpha('#ffffff', 0.3)}`,
                        cursor: 'pointer',
                        '&:hover': {
                          background: alpha(theme.palette.background.paper, 0.3),
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Stats */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              sx={{ mt: 4 }}
            >
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={700}>
                  500+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Verified Professionals
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={700}>
                  50K+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Appointments Booked
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={700}>
                  98%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Patient Satisfaction
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
};

export default MarketplaceHero;

